import React, {useEffect, useMemo, useState} from 'react';
import {IconButton} from '@ui/buttons/icon-button';
import {ArrowBackIcon} from '@ui/icons/material/ArrowBack';
import {useNavigate} from '@common/ui/navigation/use-navigate';
import {useLocation} from 'react-router-dom';
import {usePlayerOverlayStore} from '@app/web-player/state/player-overlay-store';

function useCanGoBack() {
  const location = useLocation();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // React Router v6 increments history.state.idx on SPA navigations
      const idx = (window.history?.state as any)?.idx ?? 0;
      setCanGoBack(idx > 0);
    }
  }, [location.key]);

  return canGoBack;
}

function useIsExcludedPath() {
  const {pathname} = useLocation();
  return useMemo(() => {
    if (pathname === '/') return true; // homepage
    if (pathname === '/search') return true; // search
    if (pathname.startsWith('/admin')) return true; // admin
    if (pathname.startsWith('/zy/')) return true; // zy section
    return false;
  }, [pathname]);
}

export function GlobalBackButton() {
  const navigate = useNavigate();
  const canGoBack = useCanGoBack();
  const isExcluded = useIsExcludedPath();
  const isPlayerMaximized = usePlayerOverlayStore(s => s.isMaximized);
  const isVisible = !isExcluded && canGoBack && !isPlayerMaximized;

  // Toggle helper class on body so we can offset titles on mobile via CSS
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.classList.toggle('has-global-back', isVisible);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('has-global-back');
      }
    };
  }, [isVisible]);

  // Hide when excluded routes OR no history OR player overlay is open (maximized)
  if (!isVisible) return null;

  return (
    <div className="md:hidden fixed left-16 top-16 z-40">
      <IconButton
        className="rounded-full shadow-md"
        aria-label="Voltar"
        onClick={() => (canGoBack ? navigate(-1) : navigate('/'))}
        variant="outline"
        size="md"
      >
        <ArrowBackIcon />
      </IconButton>
    </div>
  );
}
